/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */

/*
 * ImageJobQueue.jsx — คิวงานภาพสินค้า   (งานข้อ 3 · Step 4.5 ส่วน bulk)
 *
 * หน้าเมนูใหม่ในกลุ่มสินค้า (menus.code = image_job_queue — migration 0010)
 * ลูกค้าระบุหน้านี้ไว้เองในชีต 01 ว่าเป็นหน้าจอแยก ผู้ใช้คือการตลาด/จัดซื้อ
 *
 * ทำไมต้องมีหน้านี้ ไม่ใช้แท็บในหน้าสินค้าอย่างเดียว
 *   งานรอบแรก 6,662 รูป — กดทีละตัวจากหน้าสินค้าไม่ไหว
 *   หน้านี้จึงเลือกสินค้าหลายตัวพร้อมกัน แล้วสั่งงานทีเดียว
 *
 * ความปลอดภัยเรื่องเงิน (สำคัญ — งานชุดใหญ่พลาดทีเดียวเป็นพันบาท)
 *   1) จำกัด 200 รูปต่อครั้ง (backend บังคับซ้ำ)
 *   2) โชว์ค่าใช้จ่ายประเมินก่อนกด และต้องยืนยันในกล่องอีกชั้น
 *   3) backend เช็คโควตา "ทั้งชุด" ก่อนเข้าคิว — ถ้าจะเกิน บอกว่าสั่งได้กี่รูป
 *   4) ตัวกรอง "ยังไม่เคยทำภาพ" ติ๊กไว้ตั้งต้น กันสั่งซ้ำแล้วจ่ายซ้ำ
 *
 * เส้นทางที่ใช้
 *   GET     /image_job              ชนิดงาน + ราคา + สิทธิ์
 *   OPTIONS /image_job/candidates   รายการสินค้าให้เลือก (กรอง + แบ่งหน้า)
 *   OPTIONS /image_job/summary      จำนวนงานแยกตามสถานะ
 *   PUT     /image_job              สั่งงานเป็นชุด { product_ids: [...] }
 *   POST    /image_job              รายการงานในคิว
 *   OPTIONS /image_job/approve      เก็บภาพเข้าแกลเลอรี
 *   OPTIONS /image_job/retry        สั่งทำใหม่งานที่ล้มเหลว
 *   DELETE  /image_job/<id>         ยกเลิกงานที่รอคิว (worker ไม่หยิบงานที่ถูกซ่อน)
 */

import React, { useEffect, useRef, useState } from 'react';

// @mui material components
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Pagination from '@mui/material/Pagination';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDSnackbar from 'components/MDSnackbar';
import ImageReview from 'layouts/_entrio/ImageReview';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import TabPanel from 'components/TabPanel/TabPanel';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import RetryImageJobDialog from 'layouts/_entrio/RetryImageJobDialog';
import nopic from 'assets/images/nopic.jpg';
import jobCreditText, { jobErrorText, jobDateText } from 'layouts/_entrio/jobCreditText';
import {
  ReviewThumb,
  CardMenu,
  ActionRow,
  DownloadButton,
  SelectBar,
} from 'layouts/_entrio/ResultCardParts';
import useBulkDownload from 'layouts/_entrio/useBulkDownload';

const BAHT_PER_CREDIT = 0.165;

const statusList = {
  QUEUED: { name: 'รอคิว', color: 'secondary' },
  PROCESSING: { name: 'กำลังทำ', color: 'warning' },
  DONE: { name: 'สำเร็จ', color: 'success' },
  FAILED: { name: 'ไม่สำเร็จ', color: 'error' },
  CANCELLED: { name: 'ยกเลิก', color: 'secondary' },
};

const typeName = {
  BG_REMOVE: 'ลบพื้นหลัง',
  AI_CLEAN: 'AI เก็บงาน + ลบพื้นหลัง',
  SKU_STAMP: 'ใส่รหัสสินค้า',
};

const sectionSx = {
  border: '1px solid',
  borderColor: 'grey.300',
  borderRadius: 2,
  p: 3,
  bgcolor: '#ffffff',
};

const eyebrow = (text) => (
  <MDTypography
    variant='caption'
    fontWeight='bold'
    color='text'
    sx={{ letterSpacing: 1.5, textTransform: 'uppercase', opacity: 0.7 }}
  >
    {text}
  </MDTypography>
);

function ImageJobQueue() {
  const mod = 'image_job';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();

  const [tab, setTab] = useState(0);

  /* ── ฝั่งเลือกสินค้า ── */
  const [search, setSearch] = useState('');
  const [catId, setCatId] = useState(0);
  const [onlyNew, setOnlyNew] = useState(true);
  const [sellOnline, setSellOnline] = useState(true);
  const [page, setPage] = useState(1);
  const [cand, setCand] = useState({ data: [], total: 0, categories: [], max_bulk: 200 });
  const [picked, setPicked] = useState([]); // product_id ที่เลือกไว้ (ข้ามหน้าได้)
  const candRequest = useRef(0); // ตัวกรองเปลี่ยนติดกัน — ใช้ผลของคำขอล่าสุดเท่านั้น

  /* ── ฝั่งสั่งงาน ── */
  const [jobType, setJobType] = useState('BG_REMOVE');
  const [stampSku, setStampSku] = useState(false);
  const [confirm, setConfirm] = useState(false);

  /* ── ฝั่งคิวงาน ── */
  const [jobs, setJobs] = useState([]);
  const [reviewId, setReviewId] = useState(null);
  /* เก็บภาพจาก dialog ตรวจภาพแล้ว ภาพหลุดจากตัวกรอง "รอตรวจรับ" → จำตำแหน่งไว้เลื่อนไปภาพถัดไปหลังโหลดใหม่ (18 ต.ค. 2569) */
  const reviewFollow = useRef(null);
  const [retryRow, setRetryRow] = useState(null);
  const [cancelRow, setCancelRow] = useState(null);
  const [askAll, setAskAll] = useState(false); // ยืนยัน "เก็บทั้งหมดในหน้านี้" (ตรวจการใช้งาน 1.9 · 1 พ.ย. 2569)
  const [bulkBusy, setBulkBusy] = useState(false);
  const retryBusy = useRef(false);
  const [jobState, setJobState] = useState('');
  const [jobsPage, setJobsPage] = useState(1);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [approving, setApproving] = useState([]);
  const jobsLimit = 30;
  const jobsRequest = useRef(0);
  const jobsQuery = useRef({ page: 1, state: '' });
  /* คิวงานเห็นเฉพาะของตัวเอง · ผู้ดูแล AI (ai_setting) เปิด "ดูของทุกคน" ได้ (27 ต.ค. 2569 · เจ้าของงานสั่ง แบบเดียวกับกราฟิก/ภาพ AI) */
  const [canViewAll, setCanViewAll] = useState(false);
  const [scopeAll, setScopeAll] = useState(false);
  const scopeRef = useRef(false);
  jobsQuery.current = { page: jobsPage, state: jobState };
  const approvingIds = useRef(new Set());
  const approvedIds = useRef(new Set());
  const scrollAnchor = useRef(null);
  const jobsTop = useRef(null);
  const [summary, setSummary] = useState(null);

  // can_run = null จนกว่าโหลดสิทธิ์เสร็จ — กันป้าย "ดูได้อย่างเดียว" แวบขึ้นทุกครั้งที่เปิด (18 ต.ค. 2569)
  const [meta, setMeta] = useState({ job_types: [], can_run: null, usage: null });
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const notifyTimer = useRef(null);
  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }} // มุมล่างขวาบังปุ่มปิดหน้าต่างตรวจภาพ
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(null)}
      />
    );
    /* ข้อความผิดพลาดค้าง 8 วิ ให้อ่านทัน · สำเร็จ 3 วิ · ข้อความใหม่ล้างตัวจับเวลาเก่า ไม่ให้ปิดก่อนเวลา (18 ต.ค. 2569) */
    clearTimeout(notifyTimer.current);
    notifyTimer.current = setTimeout(
      function () {
        if (alive.current) setNotify(null);
      },
      color === 'error' ? 8000 : 3000
    );
  };

  /* ดาวน์โหลดภาพผลลัพธ์ ทีละภาพ / ติ๊กหลายงานข้ามหน้าเป็น zip เดียว (A2 · 25 ต.ค. 2569)
     ใน zip แยกโฟลเดอร์ตามรหัสสินค้า · ชื่อไฟล์ = รหัสสินค้า_ชนิดงาน_เลขงาน */
  const dl = useBulkDownload();
  const dlItem = (row) => {
    const skuName = row.sku || `สินค้า${row.product_id}`;
    return {
      id: row.id,
      url: row.output_url,
      folder: skuName,
      name: `${skuName}_${typeName[row.job_type] || row.job_type}_${row.id}`,
    };
  };
  const dlAll = jobs.filter((row) => row.output_url).map(dlItem);
  const dlZipName = `คิวงานภาพ_${Func.dateTimeDisplay().slice(0, 10).replace(/\//g, '-')}`;

  const loadMeta = () => {
    FetchData('GET', `/${mod}`, app.token, {})
      .then((r) => {
        if (!alive.current) return;
        if (r.status !== 'ok') {
          setLoading(false);
          showNotify(t(`alert.loaderror`), 'error');
          return;
        }
        setMeta(r);
        // บัญชีที่สั่งงานไม่ได้ เปิดมาที่แท็บคิวงานเลย (18 ต.ค. 2569) — รายการสินค้าไม่ถูกโหลด จึงปิด Backdrop ที่นี่
        if (r.can_run !== 'Y') {
          setLoading(false);
          setTab((cur) => (cur === 0 ? 1 : cur));
        }
      })
      .catch(() => {
        if (!alive.current) return;
        setLoading(false);
        showNotify(t(`alert.loaderror`), 'error');
      });
  };

  const loadCandidates = (p) => {
    candRequest.current += 1;
    const request = candRequest.current;
    setLoading(true);
    FetchData('OPTIONS', `/${mod}/candidates`, app.token, {
      page: p || page,
      limit: 24,
      search,
      cat_id: catId,
      only_new: onlyNew ? 'Y' : 'N',
      sell_online: sellOnline ? 'Y' : 'N',
      _t: Date.now(),
    })
      .then((r) => {
        if (!alive.current || request !== candRequest.current) return;
        setLoading(false);
        if (r.status === 'ok') setCand(r);
        else showNotify(t(`alert.loaderror`), 'error');
      })
      /* เน็ตหลุด/เซิร์ฟเวอร์ไม่ตอบ — ปลดม่านโหลด ไม่ให้หน้าค้าง (16 ต.ค. 2569) */
      .catch(() => {
        if (!alive.current || request !== candRequest.current) return;
        setLoading(false);
        showNotify(t(`alert.loaderror`), 'error');
      });
  };

  const loadJobs = () => {
    const query = { ...jobsQuery.current };
    jobsRequest.current += 1;
    const request = jobsRequest.current;
    setJobsLoading(true);
    const scope = scopeRef.current ? 'all' : 'mine';
    FetchData('POST', `/${mod}`, app.token, { limit: jobsLimit, ...query, scope, _t: Date.now() })
      .then((r) => {
        if (!alive.current || request !== jobsRequest.current) return;
        if (query.page !== jobsQuery.current.page || query.state !== jobsQuery.current.state)
          return;
        if (r.status === 'ok') {
          const total = Number(r.total) || 0;
          const lastPage = Math.max(1, Math.ceil(total / jobsLimit));
          setJobsTotal(total);
          if (query.page > lastPage) {
            setJobsPage(lastPage);
            return;
          }
          const rows = (r.data || []).map((row) =>
            approvedIds.current.has(row.id) ? { ...row, approved: 'Y', waiting_approval: 'N' } : row
          );
          setJobs(rows);
          setCanViewAll(r.can_view_all === 'Y');
          const follow = reviewFollow.current;
          if (follow) {
            reviewFollow.current = null;
            const shown = rows.filter((row) => row.output_url);
            // ภาพที่เพิ่งเก็บยังอยู่ (ตัวกรองทั้งหมด) = คงไว้ให้เห็นว่าเก็บแล้ว · หลุดไป = ไปภาพที่มาแทนตำแหน่งเดิม
            if (!shown.some((row) => String(row.id) === String(follow.id))) {
              if (shown.length) {
                setReviewId(shown[Math.max(0, Math.min(follow.index, shown.length - 1))].id);
              } else {
                setReviewId(null);
                showNotify('เก็บครบแล้ว · ไม่มีภาพรอตรวจรับ', 'success');
              }
            }
          }
          if (r.usage) setMeta((m) => ({ ...m, usage: r.usage }));
        } else {
          showNotify(t(`alert.loaderror`), 'error');
        }
        setJobsLoading(false);
      })
      .catch(() => {
        if (!alive.current || request !== jobsRequest.current) return;
        setJobsLoading(false);
        showNotify(t(`alert.loaderror`), 'error');
      });
    FetchData('OPTIONS', `/${mod}/summary`, app.token, { scope, _t: Date.now() })
      .then((r) => {
        if (r.status === 'ok' && alive.current && request === jobsRequest.current) setSummary(r);
      })
      .catch(() => {});
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);
    loadMeta();
    loadJobs();
  }, []);

  /* เปลี่ยนหมวด/สวิตช์ = โหลดรายการใหม่ทันทีจากหน้า 1 (เดิมต้องกดแว่นขยาย) · รันตอนเปิดหน้าด้วย (18 ต.ค. 2569) */
  useEffect(() => {
    if (meta.can_run !== 'Y') return; // รอรู้สิทธิ์ก่อน (null = ยังโหลด meta · N = ดูอย่างเดียว ไม่ต้องโหลดรายการสินค้า)
    setPage(1);
    loadCandidates(1);
  }, [catId, onlyNew, sellOnline, meta.can_run]);

  useEffect(() => {
    if (tab === 1) loadJobs();
  }, [tab, jobState, jobsPage]);

  /* งานที่ยังไม่จบ — รีเฟรชทุก 8 วินาที ระหว่างเปิดแท็บคิวงาน */
  const hasPending = jobs.some((j) => j.status === 'QUEUED' || j.status === 'PROCESSING');
  useEffect(() => {
    if (tab !== 1 || !hasPending) return undefined;
    const timer = setInterval(loadJobs, 8000);
    return () => clearInterval(timer);
  }, [tab, hasPending, jobState, jobsPage]);

  /* รีเฟรชหลังรับภาพ: คงการ์ดที่กำลังมอง ไม่เลื่อนกลับหัวหน้า */
  useEffect(() => {
    const anchor = scrollAnchor.current;
    if (!anchor || jobsLoading) return;
    const element = document.querySelector(`[data-job-id="${anchor.id}"]`);
    if (element)
      window.scrollBy({
        top: element.getBoundingClientRect().top - anchor.top,
        behavior: 'instant',
      });
    scrollAnchor.current = null;
  }, [jobs, jobsLoading]);

  const changeJobFilter = (state) => {
    if (jobState === state && jobsPage === 1) {
      loadJobs();
      return;
    }
    setJobState(state);
    setJobsPage(1);
    setJobs([]);
    scrollAnchor.current = null;
  };

  const changeJobsPage = (event, value) => {
    setJobsPage(value);
    scrollAnchor.current = null;
    if (jobsTop.current) jobsTop.current.scrollIntoView({ block: 'start' });
  };

  /* ── การเลือก ── */
  const toggle = (id) =>
    setPicked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const pageIds = cand.data.map((p) => Number(p.id));
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => picked.includes(id));

  const togglePage = () =>
    setPicked((cur) =>
      allOnPage ? cur.filter((id) => !pageIds.includes(id)) : [...new Set([...cur, ...pageIds])]
    );

  const selectedType = meta.job_types.find((x) => x.id === jobType);
  const perImage = selectedType ? Number(selectedType.credits) : 0;
  const estCredits = picked.length * perImage;
  const estBaht = (estCredits * BAHT_PER_CREDIT).toFixed(2);
  const { usage } = meta;
  const canRun = meta.can_run === 'Y' && (!usage || usage.kill_switch !== 'Y');
  const overLimit = picked.length > (cand.max_bulk || 200);

  const submit = () => {
    setConfirm(false);
    setLoading(true);

    FetchData('PUT', `/${mod}`, app.token, {
      product_ids: picked,
      job_type: jobType,
      stamp_sku: stampSku ? 'Y' : 'N',
    })
      .then((r) => {
        if (!alive.current) return;
        setLoading(false);

        if (r.status === 'ok') {
          showNotify(
            `ส่งเข้าคิว ${r.count} สินค้า · ≈ ${r.credits_expected} เครดิต ≈ ${r.baht_expected} บาท`,
            'success'
          );
          setPicked([]);
          loadCandidates();
          loadJobs();
          setTab(1);
          return;
        }

        if (r.msg === 'ai_quota_would_exceed') {
          showNotify(
            `เกินโควตาเดือนนี้ — ใช้ไป ${r.used}/${r.quota} ภาพ · ชุดนี้ต้องใช้อีก ${r.need} ภาพ · สั่งได้สูงสุด ${r.can_submit} รูป`,
            'error'
          );
        } else if (r.msg === 'bulk_too_many') {
          showNotify(`สั่งได้ครั้งละไม่เกิน ${cand.max_bulk} รูป`, 'error');
        } else {
          /* รหัสอื่นเป็นไทยจากแผนที่กลาง (โควตา · ปิดระบบ · สิทธิ์ ฯลฯ) (16 ต.ค. 2569) */
          showNotify(jobErrorText(r.msg) || t(`alert.saveerror`), 'error');
        }
      })
      .catch(() => {
        if (!alive.current) return;
        setLoading(false);
        showNotify(t(`alert.saveerror`), 'error');
      });
  };

  const reviewRows = jobs.filter((row) => row.output_url);

  const approve = (row) => {
    if (approvingIds.current.has(row.id) || row.approved === 'Y') return;
    const fromReview = String(reviewId) === String(row.id);
    const reviewIndex = reviewRows.findIndex((job) => String(job.id) === String(row.id));
    approvingIds.current.add(row.id);
    setApproving([...approvingIds.current]);
    FetchData('OPTIONS', `/${mod}/approve`, app.token, { id: row.id, _t: Date.now() })
      .then((r) => {
        if (!alive.current) return;
        approvingIds.current.delete(row.id);
        setApproving([...approvingIds.current]);
        if (r.status === 'ok' || r.msg === 'already_approved') {
          approvedIds.current.add(row.id);
          jobsRequest.current += 1; // กันผลรีเฟรชเก่าทับสถานะที่เพิ่งอนุมัติ
          setJobs((current) =>
            current.map((job) =>
              job.id === row.id ? { ...job, approved: 'Y', waiting_approval: 'N' } : job
            )
          );
          const anchor = [...document.querySelectorAll('[data-job-id]')].find(
            (el) =>
              el.getAttribute('data-job-id') !== String(row.id) &&
              el.getBoundingClientRect().top >= 110 &&
              el.getBoundingClientRect().top < window.innerHeight
          );
          const savedAnchor = anchor
            ? { id: anchor.getAttribute('data-job-id'), top: anchor.getBoundingClientRect().top }
            : null;
          showNotify(
            r.status === 'ok' ? 'เก็บเข้าแกลเลอรีแล้ว' : 'ภาพนี้อยู่ในแกลเลอรีแล้ว',
            r.status === 'ok' ? 'success' : 'warning'
          );
          if (fromReview) reviewFollow.current = { id: row.id, index: reviewIndex };
          loadJobs();
          scrollAnchor.current = savedAnchor;
        } else {
          showNotify(jobErrorText(r.msg) || t(`alert.saveerror`), 'error');
        }
      })
      .catch(() => {
        approvingIds.current.delete(row.id);
        if (!alive.current) return;
        setApproving([...approvingIds.current]);
        showNotify(t(`alert.saveerror`), 'error');
      });
  };

  const retry = (row) => {
    if (!row || retryBusy.current || meta.can_run !== 'Y' || meta.usage?.kill_switch === 'Y')
      return;
    retryBusy.current = true;
    setLoading(true);
    FetchData('OPTIONS', `/${mod}/retry`, app.token, { id: row.id, _t: Date.now() })
      .then((r) => {
        retryBusy.current = false;
        if (!alive.current) return;
        setLoading(false);
        if (r.status === 'ok') {
          setRetryRow(null);
          showNotify('สั่งทำใหม่แล้ว', 'success');
          loadJobs();
        } else {
          showNotify(jobErrorText(r.msg) || t(`alert.saveerror`), 'error');
        }
      })
      .catch(() => {
        retryBusy.current = false;
        if (alive.current) {
          setLoading(false);
          showNotify('ส่งคำขอไม่สำเร็จ กดรีเฟรชแล้วลองใหม่', 'error');
        }
      });
  };

  /* ไม่ใช้ภาพของงานที่รอตรวจรับ — ไม่นับรอตรวจรับอีก ไฟล์ผลงานยังอยู่ (ตรวจการใช้งาน 1.9 · 1 พ.ย. 2569) */
  const discard = (row) => {
    FetchData('OPTIONS', `/${mod}/discard`, app.token, { id: row.id, _t: Date.now() })
      .then((r) => {
        if (!alive.current) return;
        if (r.status === 'ok') {
          showNotify(`ไม่ใช้ภาพ ${row.sku || `งาน #${row.id}`} แล้ว`, 'success');
          loadJobs();
        } else showNotify(jobErrorText(r.msg) || t(`alert.saveerror`), 'error');
      })
      .catch(() => {
        if (alive.current) showNotify(t(`alert.saveerror`), 'error');
      });
  };

  /* เก็บทุกภาพที่รอตรวจรับในหน้านี้ — ยิงทีละงาน (approve เดิม · ทรานแซกชันต่องาน) */
  const waitingRows = jobs.filter(
    (row) => row.status === 'DONE' && row.waiting_approval === 'Y' && row.approved !== 'Y'
  );
  const approveAll = () => {
    const rows = waitingRows;
    setAskAll(false);
    setBulkBusy(true);
    let ok = 0;
    let fail = 0;
    rows
      .reduce(
        (p, row) =>
          p.then(() =>
            FetchData('OPTIONS', `/${mod}/approve`, app.token, { id: row.id, _t: Date.now() })
              .then((r) => {
                if (r.status === 'ok' || r.msg === 'already_approved') {
                  ok += 1;
                  approvedIds.current.add(row.id);
                } else fail += 1;
              })
              .catch(() => {
                fail += 1;
              })
          ),
        Promise.resolve()
      )
      .then(() => {
        if (!alive.current) return;
        setBulkBusy(false);
        jobsRequest.current += 1;
        showNotify(
          `เก็บเข้าแกลเลอรี ${ok} ภาพ${fail ? ` · ไม่สำเร็จ ${fail}` : ''}`,
          fail ? 'warning' : 'success'
        );
        loadJobs();
      });
  };

  /* ยกเลิกงานที่รอคิว = ซ่อนงาน (DELETE) — backend ไม่ยอมถ้า worker เริ่มทำแล้ว (18 ต.ค. 2569) */
  const cancelJob = () => {
    const row = cancelRow;
    if (!row) return;
    setLoading(true);
    FetchData('DELETE', `/${mod}/${row.id}`, app.token, {})
      .then((r) => {
        if (!alive.current) return;
        setLoading(false);
        setCancelRow(null);
        if (r.status === 'ok') showNotify('ยกเลิกงานแล้ว', 'success');
        else showNotify(jobErrorText(r.msg) || 'ยกเลิกไม่สำเร็จ', 'error');
        loadJobs();
      })
      .catch(() => {
        if (!alive.current) return;
        setLoading(false);
        showNotify('ยกเลิกไม่สำเร็จ', 'error');
      });
  };

  const num = (v) => Func.numberDisplay(v || 0, 0);

  const statChip = (label, value, color) => (
    <MDBox display='flex' alignItems='baseline' gap={0.75}>
      <MDTypography variant='h5' fontWeight='bold' color={color || 'dark'}>
        {num(value)}
      </MDTypography>
      <MDTypography variant='caption' color='text' sx={{ opacity: 0.8 }}>
        {label}
      </MDTypography>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.product`), 'คิวงานภาพสินค้า']} />

      <MDBox my={2}>
        {/* ── หัวหน้า ────────────────────────────────────────────────── */}
        <MDBox
          display='flex'
          alignItems='flex-start'
          justifyContent='space-between'
          flexWrap='wrap'
          gap={1}
          mb={1}
        >
          <MDBox>
            <MDTypography variant='h5' fontWeight='medium'>
              คิวงานภาพสินค้า
            </MDTypography>
          </MDBox>
          <MDBox display='flex' gap={1} alignItems='center' flexWrap='wrap'>
            {usage && (
              <Chip
                size='small'
                variant='outlined'
                label={`เดือนนี้ทั้งร้าน ${usage.month_images}${
                  usage.quota > 0 ? `/${usage.quota}` : ''
                } ภาพ${usage.my_images != null ? ` · คุณ ${usage.my_images}` : ''}`}
              />
            )}
            {usage && usage.kill_switch === 'Y' && (
              <Chip size='small' color='error' label='AI ปิดทั้งร้าน' />
            )}
          </MDBox>
        </MDBox>
        {/* บัญชีดูอย่างเดียว — ป้ายชัด ๆ และไม่เปิดแท็บสั่งงานให้ (18 ต.ค. 2569) */}
        {meta.can_run === 'N' && (
          <MDBox
            mb={2}
            px={1.5}
            py={1.25}
            display='flex'
            gap={1}
            alignItems='flex-start'
            role='status'
            sx={{ borderRadius: 2, bgcolor: 'rgba(251,140,0,0.12)', color: '#9A4D00' }}
          >
            <Icon fontSize='small' sx={{ mt: '2px' }}>
              visibility
            </Icon>
            <MDTypography variant='button' fontWeight='medium' color='inherit' lineHeight={1.45}>
              บัญชีนี้ยังสั่งงานภาพไม่ได้ · ขอสิทธิ์จากผู้ดูแลระบบ
            </MDTypography>
          </MDBox>
        )}

        {/* งานค้างคิวเกิน 10 นาทีและตัวทำภาพไม่ขยับเลย = cron บนเซิร์ฟเวอร์น่าจะไม่ทำงาน (ตรวจการใช้งาน 1.3 · 1 พ.ย. 2569) */}
        {summary && Number(summary.queue_stale) > 0 && (
          <MDBox
            mb={2}
            px={1.5}
            py={1.25}
            display='flex'
            gap={1}
            alignItems='flex-start'
            role='alert'
            sx={{ borderRadius: 2, bgcolor: 'rgba(244,67,53,0.1)', color: '#B3261E' }}
          >
            <Icon fontSize='small' sx={{ mt: '2px' }}>
              hourglass_disabled
            </Icon>
            <MDTypography variant='button' fontWeight='medium' color='inherit' lineHeight={1.45}>
              มี {Func.numberDisplay(summary.queue_stale, 0)} งานรอคิวเกิน 10 นาที
              ระบบทำภาพอาจหยุดทำงาน แจ้งไอที · งานที่ไม่ต้องการยกเลิกได้ในแท็บคิวงาน
            </MDTypography>
          </MDBox>
        )}

        {/* ── แถบตัวเลขสรุป ──────────────────────────────────────────── */}
        {summary && (
          <Card
            sx={{ p: 2, mb: 2, boxShadow: 'none', border: '1px solid', borderColor: 'grey.300' }}
          >
            {/* ตัวเลขนับตามที่เห็นในรายการ — ของตัวเอง หรือของทุกคนเมื่อเปิด "ดูของทุกคน" (รีเช็ค 27 ต.ค. 2569) */}
            <MDTypography variant='caption' color='text' fontWeight='bold' display='block' mb={1}>
              {scopeAll ? 'งานของทุกคน' : 'งานของคุณ'}
            </MDTypography>
            <MDBox display='flex' gap={4} flexWrap='wrap'>
              {statChip('รอคิว', summary.by_status.QUEUED)}
              {statChip('กำลังทำ', summary.by_status.PROCESSING, 'warning')}
              <MDButton
                variant='text'
                color='info'
                onClick={() => {
                  changeJobFilter('WAITING_APPROVAL');
                  setTab(1);
                }}
                aria-label={`รอตรวจรับ ${summary.waiting_approve} งาน`}
              >
                {statChip('รอตรวจรับ →', summary.waiting_approve, 'info')}
              </MDButton>
              {statChip('สำเร็จ', summary.by_status.DONE, 'success')}
              {statChip('ไม่สำเร็จ', summary.by_status.FAILED, 'error')}
              <MDBox flexGrow={1} />
              {/* ยอดสะสมของทุกงานในรายการ ไม่ใช่ของเดือนนี้ — ป้ายบอกให้ชัด (18 ต.ค. 2569) */}
              <MDBox display='flex' alignItems='baseline' gap={0.75}>
                <MDTypography variant='h5' fontWeight='bold'>
                  ≈ {Func.numberDisplay(summary.baht_total || 0, 2)}
                </MDTypography>
                <MDTypography variant='caption' color='text' sx={{ opacity: 0.8 }}>
                  {scopeAll ? 'บาท · งานของทุกคนรวมทั้งหมด' : 'บาท · งานของคุณรวมทั้งหมด'}
                </MDTypography>
              </MDBox>
            </MDBox>
          </Card>
        )}

        <Card sx={{ p: 2 }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} aria-label='tabs-image-job'>
            <Tab label='เลือกสินค้าสั่งงาน' disabled={meta.can_run === 'N'} />
            <Tab label='คิวงาน' />
          </Tabs>

          {/* ══════════ แท็บ 1 · เลือกสินค้า ══════════ */}
          <TabPanel value={tab} index={0}>
            <MDBox sx={sectionSx} mb={2}>
              {eyebrow('ค้นหาสินค้า')}
              <Grid container spacing={2} mt={0.5}>
                <Grid item xs={12} md={3}>
                  <MDInput
                    label='รหัสหรือชื่อสินค้า'
                    fullWidth
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setPage(1);
                        loadCandidates(1);
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDInput
                    label='หมวดสินค้า'
                    fullWidth
                    select
                    value={catId}
                    onChange={(e) => setCatId(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value={0}>ทุกหมวด</MenuItem>
                    {cand.categories.map((c) => (
                      <MenuItem key={c.id} value={Number(c.id)}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </MDInput>
                </Grid>
                <Grid item xs={6} md={3} display='flex' alignItems='center'>
                  <FormControlLabel
                    control={
                      <Switch checked={onlyNew} onChange={(e) => setOnlyNew(e.target.checked)} />
                    }
                    title='ซ่อนสินค้าที่มีคนในร้านสั่งงานภาพไปแล้ว กันจ่ายเครดิตซ้ำ'
                    label={
                      <MDTypography variant='button' color='text'>
                        ยังไม่มีใครทำภาพ
                      </MDTypography>
                    }
                  />
                </Grid>
                <Grid item xs={6} md={2} display='flex' alignItems='center'>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sellOnline}
                        onChange={(e) => setSellOnline(e.target.checked)}
                      />
                    }
                    label={
                      <MDTypography variant='button' color='text'>
                        ขายออนไลน์
                      </MDTypography>
                    }
                  />
                </Grid>
                <Grid item xs={12} md={1} display='flex' alignItems='center'>
                  <MDButton
                    variant='gradient'
                    color='dark'
                    fullWidth
                    aria-label='ค้นหา'
                    title='ค้นหา'
                    onClick={() => {
                      setPage(1);
                      loadCandidates(1);
                    }}
                  >
                    <Icon>search</Icon>
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>

            {/* แถบสั่งงาน — ค้างอยู่เหนือรายการ เห็นตลอดว่าเลือกไปกี่ตัว */}
            <MDBox
              sx={{
                border: '1px solid',
                borderColor: picked.length > 0 ? 'grey.500' : 'grey.300',
                borderRadius: 2,
                p: 2,
                mb: 2,
                bgcolor: picked.length > 0 ? 'grey.100' : '#ffffff',
              }}
            >
              <Grid container spacing={2} alignItems='center'>
                <Grid item xs={12} md={3}>
                  <MDTypography variant='h6' fontWeight='medium'>
                    เลือกแล้ว {num(picked.length)} สินค้า
                  </MDTypography>
                  <MDTypography variant='caption' color='text' sx={{ opacity: 0.75 }}>
                    จากทั้งหมด {num(cand.total)} รายการที่ค้นเจอ
                  </MDTypography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDInput
                    label='ชนิดงาน'
                    fullWidth
                    select
                    value={jobType}
                    onChange={(e) => setJobType(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  >
                    {meta.job_types.map((x) => (
                      <MenuItem key={x.id} value={x.id}>
                        {x.name} · {x.credits > 0 ? `${x.credits} เครดิต` : 'ฟรี'}
                      </MenuItem>
                    ))}
                  </MDInput>
                </Grid>
                <Grid item xs={6} md='auto' display='flex' alignItems='center'>
                  <FormControlLabel
                    sx={{ m: 0, whiteSpace: 'nowrap' }}
                    control={
                      <Switch
                        checked={stampSku || jobType === 'SKU_STAMP'}
                        disabled={jobType === 'SKU_STAMP'}
                        onChange={(e) => setStampSku(e.target.checked)}
                      />
                    }
                    label={
                      <MDTypography variant='button' color='text'>
                        ใส่รหัสสินค้าด้วย
                      </MDTypography>
                    }
                  />
                </Grid>
                <Grid item xs={6} md>
                  <MDTypography
                    variant='button'
                    fontWeight='bold'
                    color={overLimit ? 'error' : 'dark'}
                  >
                    ≈ {estBaht} บาท
                  </MDTypography>
                  <MDTypography
                    variant='caption'
                    color='text'
                    display='block'
                    sx={{ opacity: 0.75 }}
                  >
                    {num(estCredits)} เครดิต
                  </MDTypography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <MDButton
                    variant='gradient'
                    color='dark'
                    fullWidth
                    disabled={!canRun || picked.length === 0 || overLimit}
                    onClick={() => setConfirm(true)}
                  >
                    <Icon>play_arrow</Icon>&nbsp;สั่งงาน
                  </MDButton>
                </Grid>
                {overLimit && (
                  <Grid item xs={12}>
                    <MDTypography variant='caption' color='error'>
                      เลือกเกิน {cand.max_bulk} รูป — เอาออกบางส่วนก่อน
                    </MDTypography>
                  </Grid>
                )}
              </Grid>
            </MDBox>

            {/* รายการสินค้า */}
            <MDBox display='flex' alignItems='center' gap={1} mb={1}>
              <MDButton size='small' variant='outlined' color='dark' onClick={togglePage}>
                <Icon>{allOnPage ? 'check_box' : 'check_box_outline_blank'}</Icon>
                &nbsp;{allOnPage ? 'เอาหน้านี้ออก' : 'เลือกทั้งหน้า'}
              </MDButton>
              {picked.length > 0 && (
                <MDButton size='small' variant='text' color='error' onClick={() => setPicked([])}>
                  ล้างที่เลือกทั้งหมด
                </MDButton>
              )}
            </MDBox>

            <Grid container spacing={1.5}>
              {cand.data.map((p) => {
                const on = picked.includes(Number(p.id));
                return (
                  <Grid item xs={6} sm={4} md={2} key={p.id}>
                    {/* การ์ดทั้งใบเป็นช่องติ๊ก — Tab มาถึงและกด Space/Enter ได้ (18 ต.ค. 2569) */}
                    <Card
                      role='checkbox'
                      aria-checked={on}
                      aria-label={`${p.sku} ${p.name}`}
                      tabIndex={0}
                      onClick={() => toggle(Number(p.id))}
                      onKeyDown={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                          e.preventDefault();
                          toggle(Number(p.id));
                        }
                      }}
                      sx={{
                        cursor: 'pointer',
                        boxShadow: 'none',
                        border: '2px solid',
                        borderColor: on ? 'info.main' : 'grey.300',
                        overflow: 'hidden',
                        '&:focus-visible': {
                          outline: '2px solid',
                          outlineColor: 'info.main',
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <MDBox sx={{ position: 'relative' }}>
                        <MDBox
                          sx={{
                            aspectRatio: '1 / 1',
                            bgcolor: 'grey.100',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <img
                            src={p.image_url}
                            alt={p.sku}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = nopic;
                            }}
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                        </MDBox>
                        <Icon
                          aria-hidden='true'
                          color={on ? 'info' : 'disabled'}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            bgcolor: 'rgba(255,255,255,0.85)',
                            borderRadius: 1,
                            fontSize: 24,
                          }}
                        >
                          {on ? 'check_box' : 'check_box_outline_blank'}
                        </Icon>
                      </MDBox>
                      <MDBox p={1}>
                        <MDTypography variant='caption' fontWeight='bold' display='block'>
                          {p.sku}
                        </MDTypography>
                        <MDTypography
                          variant='caption'
                          color='text'
                          sx={{
                            opacity: 0.75,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            minHeight: '2.1em',
                          }}
                        >
                          {p.name}
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>
                );
              })}
              {cand.data.length === 0 && (
                <Grid item xs={12}>
                  <MDBox
                    py={5}
                    display='flex'
                    flexDirection='column'
                    alignItems='center'
                    gap={1}
                    sx={{ border: '1px dashed', borderColor: 'grey.400', borderRadius: 2 }}
                  >
                    <Icon sx={{ fontSize: 36, opacity: 0.3 }}>search_off</Icon>
                    <MDTypography variant='body2' color='text' sx={{ opacity: 0.7 }}>
                      ไม่พบสินค้า — ลองปิดตัวกรอง &quot;ยังไม่มีใครทำภาพ&quot;
                    </MDTypography>
                  </MDBox>
                </Grid>
              )}
            </Grid>

            {cand.total > 24 && (
              <MDBox display='flex' justifyContent='center' mt={3}>
                <Pagination
                  count={Math.ceil(cand.total / 24)}
                  page={page}
                  onChange={(e, v) => {
                    setPage(v);
                    loadCandidates(v);
                  }}
                  color='info'
                  siblingCount={1}
                />
              </MDBox>
            )}
          </TabPanel>

          {/* ══════════ แท็บ 2 · คิวงาน ══════════ */}
          <TabPanel value={tab} index={1}>
            <MDBox
              ref={jobsTop}
              sx={{ scrollMarginTop: 120 }}
              display='flex'
              gap={1}
              mb={2}
              flexWrap='wrap'
            >
              {[
                ['', 'ทั้งหมด'],
                ['WAITING_APPROVAL', 'รอตรวจรับ'],
                ['QUEUED', 'รอคิว'],
                ['PROCESSING', 'กำลังทำ'],
                ['DONE', 'สำเร็จ'],
                ['FAILED', 'ไม่สำเร็จ'],
              ].map(([v, label]) => (
                <MDButton
                  key={label}
                  size='small'
                  variant={jobState === v ? 'gradient' : 'outlined'}
                  color='dark'
                  onClick={() => changeJobFilter(v)}
                >
                  {label}
                </MDButton>
              ))}
              {meta.can_run === 'Y' && waitingRows.length > 1 && (
                <MDButton
                  size='small'
                  variant='gradient'
                  color='info'
                  disabled={bulkBusy}
                  onClick={() => setAskAll(true)}
                >
                  <Icon>collections</Icon>&nbsp;
                  {bulkBusy ? 'กำลังเก็บ…' : `เก็บทั้งหมดในหน้านี้ (${waitingRows.length})`}
                </MDButton>
              )}
              {canViewAll && (
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={
                    <Switch
                      size='small'
                      checked={scopeAll}
                      onChange={(e) => {
                        scopeRef.current = e.target.checked;
                        setScopeAll(e.target.checked);
                        dl.clear();
                        if (jobsPage === 1) loadJobs();
                        else setJobsPage(1);
                      }}
                    />
                  }
                  label={<MDTypography variant='caption'>ดูของทุกคน</MDTypography>}
                />
              )}
              <MDBox flexGrow={1} />
              <MDButton size='small' variant='text' color='dark' onClick={loadJobs}>
                <Icon>refresh</Icon>&nbsp;รีเฟรช
              </MDButton>
            </MDBox>

            <MDBox
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              flexWrap='wrap'
              gap={1}
              mb={2}
            >
              <MDTypography variant='caption' color='text' role='status'>
                {jobsLoading && jobs.length === 0
                  ? 'กำลังโหลดรายการ…'
                  : `${jobsTotal ? (jobsPage - 1) * jobsLimit + 1 : 0}–${Math.min(
                      jobsPage * jobsLimit,
                      jobsTotal
                    )} จาก ${num(jobsTotal)} งาน`}
              </MDTypography>
              <MDBox display='flex' alignItems='center' gap={1} flexWrap='wrap'>
                <SelectBar
                  on={dl.on}
                  onToggle={dl.toggleMode}
                  selected={dl.count}
                  total={dlAll.length}
                  pageSelected={dlAll.filter((it) => dl.isSelected(it.id)).length}
                  onAll={() => dl.addAll(dlAll)}
                  onNone={dl.clear}
                  onDownload={() => dl.run(dlZipName, showNotify)}
                  busy={dl.busy}
                  progress={dl.progress}
                />
                {jobsTotal > jobsLimit && (
                  <Pagination
                    count={Math.ceil(jobsTotal / jobsLimit)}
                    page={jobsPage}
                    onChange={changeJobsPage}
                    color='info'
                    aria-label='หน้าคิวงาน'
                  />
                )}
              </MDBox>
            </MDBox>

            <Grid container spacing={2}>
              {jobs.map((row) => {
                const st = statusList[row.status] || { name: row.status, color: 'default' };
                const working = row.status === 'QUEUED' || row.status === 'PROCESSING';
                const saved = row.approved === 'Y';
                /* ปุ่มที่เขียนข้อมูล (ยกเลิก/ทำใหม่/เก็บ) เฉพาะบัญชีที่มีสิทธิ์สั่งงาน · ดาวน์โหลดได้ทุกบัญชี (25 ต.ค. 2569) */
                const canAct =
                  meta.can_run === 'Y' &&
                  (row.status === 'FAILED' ||
                    row.status === 'QUEUED' ||
                    (row.status === 'DONE' && !saved && row.waiting_approval === 'Y'));
                /* ป้ายบนรูปขึ้นเฉพาะสถานะที่ต้องรู้ · กดที่รูป = ตรวจภาพ (18 ต.ค. 2569) */
                let badge = null;
                if (saved) badge = <Chip size='small' color='success' label='อยู่ในแกลเลอรีแล้ว' />;
                // มีปุ่มเก็บเข้าแกลเลอรีอยู่แล้วไม่ต้องขึ้นป้ายรอตรวจรับซ้ำ (ขึ้นเฉพาะบัญชีที่ไม่มีสิทธิ์กด)
                else if (row.waiting_approval === 'Y' && meta.can_run === 'N')
                  badge = <Chip size='small' color='info' label='รอตรวจรับ' />;
                else if (!working && row.status !== 'DONE') {
                  badge = (
                    <Chip
                      size='small'
                      color={st.color === 'default' ? undefined : st.color}
                      label={st.name}
                    />
                  );
                }
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={row.id} data-job-id={row.id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        boxShadow: 'none',
                        overflow: 'hidden',
                      }}
                    >
                      <ReviewThumb
                        onReview={row.output_url ? () => setReviewId(row.id) : null}
                        label={`ตรวจภาพ ${row.sku}`}
                        select={
                          row.output_url ? dl.selectProps(dlItem(row), `เลือกภาพ ${row.sku}`) : null
                        }
                        sx={{ display: 'flex', borderBottom: '1px solid', borderColor: 'grey.200' }}
                      >
                        <MDBox
                          sx={{
                            width: '50%',
                            aspectRatio: '1 / 1',
                            bgcolor: 'grey.100',
                            borderRight: '1px solid',
                            borderColor: 'grey.200',
                          }}
                        >
                          {row.input_url && (
                            <img
                              src={row.input_url}
                              alt='in'
                              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                          )}
                        </MDBox>
                        <MDBox
                          sx={{
                            width: '50%',
                            aspectRatio: '1 / 1',
                            bgcolor: 'grey.100',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {row.output_url ? (
                            <img
                              src={row.output_url}
                              alt='out'
                              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                          ) : working ? (
                            <MDBox
                              display='flex'
                              flexDirection='column'
                              alignItems='center'
                              gap={0.75}
                            >
                              <CircularProgress size={20} color='inherit' sx={{ opacity: 0.4 }} />
                              <MDTypography variant='caption' color='text' sx={{ opacity: 0.6 }}>
                                {st.name}
                              </MDTypography>
                            </MDBox>
                          ) : (
                            <Icon sx={{ fontSize: 26, opacity: 0.25 }}>block</Icon>
                          )}
                        </MDBox>
                      </ReviewThumb>

                      <MDBox p={1.5} display='flex' flexDirection='column' gap={0.5} flexGrow={1}>
                        <MDBox display='flex' alignItems='center' gap={0.75} flexWrap='wrap'>
                          <MDTypography variant='caption' fontWeight='bold'>
                            {row.sku}
                          </MDTypography>
                          {badge}
                        </MDBox>
                        <MDTypography variant='caption' color='text' sx={{ opacity: 0.75 }}>
                          {typeName[row.job_type] || row.job_type}
                          {row.stamp_sku === 'Y' ? ' + ใส่รหัส' : ''}
                          {` · ${jobCreditText(row, meta.job_types)}`}
                          {scopeAll ? ` · ${row.created_by_name || 'ระบบ'}` : ''}
                        </MDTypography>
                        {/* วันเวลาบรรทัดเดียว — ชี้เมาส์ดูเวลาเต็ม (รอบตัดคำ 6 ต.ค. 2569) */}
                        <MDTypography
                          variant='caption'
                          color='text'
                          sx={{ opacity: 0.6 }}
                          title={`สั่งเมื่อ ${jobDateText(row.created_at, true)}${
                            row.finished_at
                              ? ` · เสร็จเมื่อ ${jobDateText(row.finished_at, true)}`
                              : ''
                          }`}
                        >
                          {row.finished_at
                            ? `เสร็จ ${jobDateText(row.finished_at)}`
                            : `สั่ง ${jobDateText(row.created_at)}`}
                        </MDTypography>
                        {/* เหตุผลภาษาไทย · รหัสดิบอยู่ใน title ให้ชี้เมาส์ดู (16 ต.ค. 2569) */}
                        {row.status === 'FAILED' && row.error_msg && (
                          <MDTypography variant='caption' color='error' title={row.error_msg}>
                            {jobErrorText(row.error_msg) || 'เกิดข้อผิดพลาด'}
                          </MDTypography>
                        )}
                        {/* ปุ่มที่เขียนข้อมูลซ่อนเมื่อบัญชีไม่มีสิทธิ์สั่งงาน — backend ปฏิเสธอยู่แล้ว (16 ต.ค. 2569)
                            งานรอคิวยกเลิกได้จากเมนู ⋯ · งานที่กำลังทำไม่มีปุ่ม (18 ต.ค. 2569) */}
                        {(canAct || row.output_url) && (
                          <MDBox mt='auto' pt={1}>
                            <ActionRow>
                              {canAct && row.status === 'QUEUED' ? (
                                <>
                                  <MDBox flexGrow={1} />
                                  <CardMenu
                                    label={`ตัวเลือกเพิ่มเติมของงาน ${row.id}`}
                                    items={[
                                      {
                                        label: 'ยกเลิก',
                                        icon: 'cancel',
                                        danger: true,
                                        onClick: () => setCancelRow(row),
                                      },
                                    ]}
                                  />
                                </>
                              ) : canAct && row.status === 'FAILED' ? (
                                <MDButton
                                  size='small'
                                  variant='outlined'
                                  color='dark'
                                  disabled={!canRun}
                                  onClick={() => setRetryRow(row)}
                                  data-primary=''
                                  sx={{ flexGrow: 1, minWidth: 0, px: 1 }}
                                >
                                  <Icon>refresh</Icon>&nbsp;ทำใหม่
                                </MDButton>
                              ) : canAct ? (
                                <MDButton
                                  size='small'
                                  variant='gradient'
                                  color='info'
                                  disabled={approving.includes(row.id)}
                                  aria-label={`เก็บภาพ ${row.sku} เข้าแกลเลอรี`}
                                  onClick={() => approve(row)}
                                  data-primary=''
                                  sx={{ flexGrow: 1, minWidth: 0, px: 1 }}
                                >
                                  <Icon>collections</Icon>&nbsp;
                                  {approving.includes(row.id) ? 'กำลังเก็บ…' : 'เก็บเข้าแกลเลอรี'}
                                </MDButton>
                              ) : (
                                <MDBox flexGrow={1} />
                              )}
                              {row.output_url && (
                                <DownloadButton
                                  label={`ดาวน์โหลดภาพ ${row.sku} งาน ${row.id}`}
                                  onClick={() =>
                                    dl.one(row.output_url, dlItem(row).name, showNotify)
                                  }
                                />
                              )}
                              {canAct && row.status === 'DONE' && !saved && (
                                <CardMenu
                                  label={`ตัวเลือกเพิ่มเติมของงาน ${row.id}`}
                                  items={[
                                    {
                                      label: 'ไม่ใช้ภาพนี้',
                                      icon: 'block',
                                      danger: true,
                                      onClick: () => discard(row),
                                    },
                                  ]}
                                />
                              )}
                            </ActionRow>
                          </MDBox>
                        )}
                      </MDBox>
                    </Card>
                  </Grid>
                );
              })}
              {jobs.length === 0 && !jobsLoading && (
                <Grid item xs={12}>
                  <MDBox
                    py={5}
                    display='flex'
                    flexDirection='column'
                    alignItems='center'
                    gap={1}
                    sx={{ border: '1px dashed', borderColor: 'grey.400', borderRadius: 2 }}
                  >
                    <Icon sx={{ fontSize: 36, opacity: 0.3 }}>inbox</Icon>
                    <MDTypography variant='body2' color='text' sx={{ opacity: 0.7 }}>
                      {/* เห็นเฉพาะของตัวเอง + ยังไม่เคยสั่ง = บอกตรง ๆ (ตรวจใน Chrome 27 ต.ค.: "ไม่พบงานตามเงื่อนไขนี้" ชวนงงว่างานหาย) */}
                      {jobState === 'WAITING_APPROVAL'
                        ? 'ไม่มีภาพรอตรวจรับ'
                        : jobState === '' && !scopeAll
                        ? `คุณยังไม่ได้สั่งงานภาพ${
                            canRun
                              ? ' — เลือกสินค้าที่แท็บ "เลือกสินค้าสั่งงาน"'
                              : canViewAll
                              ? ' — เปิด "ดูของทุกคน" เพื่อดูงานของคนอื่น'
                              : ' · บัญชีนี้เห็นเฉพาะงานที่ตัวเองสั่ง'
                          }`
                        : 'ไม่พบงานตามเงื่อนไขนี้'}
                    </MDTypography>
                  </MDBox>
                </Grid>
              )}
            </Grid>
            {jobsTotal > jobsLimit && (
              <MDBox display='flex' justifyContent='center' mt={3}>
                <Pagination
                  count={Math.ceil(jobsTotal / jobsLimit)}
                  page={jobsPage}
                  onChange={changeJobsPage}
                  color='info'
                  aria-label='หน้าคิวงานด้านล่าง'
                />
              </MDBox>
            )}
          </TabPanel>
        </Card>
      </MDBox>

      {/* ── กล่องยืนยันก่อนเสียเงิน ────────────────────────────────────── */}
      <Dialog open={confirm} onClose={() => setConfirm(false)} maxWidth='xs' fullWidth>
        <DialogTitle>ยืนยันสั่งงาน {num(picked.length)} สินค้า</DialogTitle>
        <DialogContent>
          <MDBox display='flex' flexDirection='column' gap={1}>
            <MDTypography variant='body2' color='text'>
              ชนิดงาน: <b>{selectedType ? selectedType.name : jobType}</b>
              {stampSku && jobType !== 'SKU_STAMP' ? ' + ใส่รหัส' : ''}
            </MDTypography>
            <MDTypography variant='body2' color='text'>
              ค่าใช้จ่ายโดยประมาณ:{' '}
              <b>
                {num(estCredits)} เครดิต ≈ {estBaht} บาท
              </b>
            </MDTypography>
            <Divider sx={{ my: 1 }} />
            <MDTypography variant='caption' color='text' sx={{ opacity: 0.8 }}>
              ภาพที่ได้ต้องกดเก็บเข้าแกลเลอรีก่อน รูปเดิมของสินค้าไม่ถูกทับ
              {jobType === 'AI_CLEAN' ? ' · AI เก็บงานวาดส่วนที่ถูกบังใหม่ ตรวจทุกใบ' : ''}
              {jobType === 'SKU_STAMP'
                ? ' · รูปที่เป็นการ์ดมีรหัสอยู่แล้ว รหัสจะซ้อน 2 ชั้น ให้ใช้กับรูปถ่ายเปล่า'
                : ''}
            </MDTypography>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant='outlined' color='secondary' onClick={() => setConfirm(false)}>
            {t(`element.cancel`)}
          </MDButton>
          <MDButton variant='gradient' color='dark' onClick={submit}>
            <Icon>play_arrow</Icon>&nbsp;ยืนยันสั่งงาน
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
      <RetryImageJobDialog
        row={retryRow}
        types={meta.job_types}
        usage={usage}
        busy={loading}
        allowed={canRun}
        onClose={() => setRetryRow(null)}
        onConfirm={retry}
      />
      <Dialog open={askAll} onClose={() => setAskAll(false)} maxWidth='xs' fullWidth>
        <DialogTitle>เก็บทั้งหมดในหน้านี้?</DialogTitle>
        <DialogContent>
          <MDTypography variant='body2' color='text'>
            เก็บ {waitingRows.length} ภาพที่รอตรวจรับในหน้านี้เข้าแกลเลอรีสินค้า
          </MDTypography>
          <MDTypography variant='caption' color='text'>
            ภาพที่ไม่เอา กด ⋯ › ไม่ใช้ภาพนี้ ก่อน · สินค้าที่ยังไม่มีรูปหลักจะตั้งภาพนี้เป็นรูปหลัก
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant='outlined' color='secondary' autoFocus onClick={() => setAskAll(false)}>
            กลับ
          </MDButton>
          <MDButton variant='gradient' color='info' onClick={approveAll}>
            <Icon>collections</Icon>&nbsp;เก็บ {waitingRows.length} ภาพ
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(cancelRow)}
        onClose={loading ? undefined : () => setCancelRow(null)}
        maxWidth='xs'
        fullWidth
        aria-labelledby='cancel-job-title'
      >
        <DialogTitle id='cancel-job-title'>ยกเลิกงานนี้?</DialogTitle>
        <DialogContent>
          <MDTypography variant='body2' color='text'>
            {cancelRow
              ? `${cancelRow.sku || `สินค้า #${cancelRow.product_id}`} · ${
                  typeName[cancelRow.job_type] || cancelRow.job_type
                }`
              : ''}
          </MDTypography>
          <MDTypography variant='caption' color='text'>
            งานจะไม่ถูกทำ และหายจากรายการ
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant='outlined'
            color='secondary'
            disabled={loading}
            onClick={() => setCancelRow(null)}
          >
            กลับ
          </MDButton>
          <MDButton variant='gradient' color='error' disabled={loading} onClick={cancelJob}>
            <Icon>cancel</Icon>&nbsp;ยกเลิกงาน
          </MDButton>
        </DialogActions>
      </Dialog>
      <ImageReview
        items={reviewRows.map((row) => ({
          id: row.id,
          title: `${row.sku || `สินค้า #${row.product_id}`} · งาน #${row.id}`,
          src: row.output_url,
          fileName: dlItem(row).name,
          beforeSrc: row.input_url,
          approved: row.approved === 'Y',
          canApprove: row.status === 'DONE' && row.waiting_approval === 'Y' && meta.can_run === 'Y',
          row,
        }))}
        selectedId={reviewId}
        onChange={(nextId) => {
          reviewFollow.current = null;
          setReviewId(nextId);
        }}
        onClose={() => {
          reviewFollow.current = null;
          setReviewId(null);
        }}
        onApprove={approve}
        busy={approving.includes(reviewId)}
      />
      {notify}

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </DashboardLayout>
  );
}

export default ImageJobQueue;
